import CreateIcon from "@material-ui/icons/Create"
import produce from "immer"
import type { LatLng, Map as LeafletMap, LeafletMouseEvent } from "leaflet"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { useTranslation } from "react-i18next"
import { useMap } from "react-leaflet"
import { connect } from "react-redux"
import type {
  AppState,
  AppThunkDispatch,
  IMapDrawing,
  IPolyline,
  MapViewerActions,
  ProjectActions
} from "../../../redux"
import {
  MapViewerPanel,
  mapViewerSwitchEditPanel,
  mapViewerTogglePanel,
  projectAddDrawing,
  projectAddPointToPolyline,
  projectRemoveDrawing
} from "../../../redux"
import { createNewDrawingId } from "../../../utils"
import { TooltipIconButton } from "../../atoms"

export interface IDrawPencilControlStateProps {
  canDraw: boolean
  color: string
  toggleDrawing: (open: boolean) => void
  selectControl: () => void
  addDrawing: (drawing: IMapDrawing) => void
  editDrawing: (drawing: IMapDrawing) => void
  enableSelectionTool: () => void
}

export interface IDrawingState {
  mouseDown: boolean
  saveInterval: number
  eventCount: number
  points: LatLng[]
  drawing?: IMapDrawing
}

function DrawPencilControlView(props: IDrawPencilControlStateProps): ReactElement {
  const { canDraw, color, toggleDrawing, selectControl, addDrawing, editDrawing, enableSelectionTool } = props

  const { t } = useTranslation("map")

  const [drawState, setDrawState] = useState<IDrawingState>({
    mouseDown: false,
    saveInterval: 2,
    eventCount: 0,
    points: []
  })

  const map: LeafletMap = useMap()
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    map.on("mousedown", startDrawing)
    map.on("mouseup", endDrawing)
    map.on("mousemove", processMouseMovement)

    return () => {
      map.off("mousedown", startDrawing)
      map.off("mouseup", endDrawing)
      map.off("mousemove", processMouseMovement)
    }
  }, [canDraw, color, drawState])

  function controlClicked(): void {
    if (canDraw) {
      toggleDrawing(false)
    } else {
      selectControl()
    }
  }

  function startDrawing(event: LeafletMouseEvent): void {
    if (!drawState.mouseDown && canDraw) {
      map.dragging.disable()

      const point: LatLng = event.latlng
      const drawing: IMapDrawing = {
        id: createNewDrawingId(),
        isPopupOpen: false,
        shape: {
          type: "polyline",
          color: color,
          lineWidth: 4,
          positions: [point],
          finished: false
        } as IPolyline
      }

      addDrawing(drawing)

      setDrawState({
        ...drawState,
        mouseDown: true,
        eventCount: 0,
        points: [],
        drawing
      })
    }
  }

  function endDrawing(): void {
    if (drawState.mouseDown && canDraw && drawState.drawing) {
      map.dragging.enable()

      const newDrawing: IMapDrawing = produce(drawState.drawing, (draft: IMapDrawing) => {
        draft.isPopupOpen = true
        ;(draft.shape as IPolyline).finished = true
      })
      enableSelectionTool()
      editDrawing(newDrawing)
      setDrawState({
        ...drawState,
        mouseDown: false,
        drawing: undefined
      })
    }
  }

  function processMouseMovement(event: LeafletMouseEvent): void {
    if (!drawState.mouseDown || !canDraw || !drawState.drawing) return

    const point: LatLng = event.latlng
    const points: LatLng[] = [...drawState.points]
    if (drawState.eventCount % drawState.saveInterval === 0 || drawState.eventCount === 0) {
      points.push(point)
    }

    const newDrawing: IMapDrawing = produce(drawState.drawing, (draft: IMapDrawing) => {
      ;(draft.shape as IPolyline).positions = points
    })
    setDrawState({
      ...drawState,
      eventCount: drawState.eventCount + 1,
      points,
      drawing: newDrawing
    })

    editDrawing(newDrawing)
  }

  return (
    <>
      <TooltipIconButton
        onClick={controlClicked}
        selected={canDraw}
        placement="right"
        tooltip={t("actionButtons.edit.penTool")}
        icon={<CreateIcon />}
      />
    </>
  )
}

function mapStateToProps(state: AppState) {
  return {
    canDraw: state.mapViewer.panels.get(MapViewerPanel.DrawPencil) || false,
    color: state.colorPicker.color
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ProjectActions | MapViewerActions>) {
  return {
    toggleDrawing: (open: boolean) => dispatch(mapViewerTogglePanel(MapViewerPanel.DrawPencil, open)),
    selectControl: () => dispatch(mapViewerSwitchEditPanel(MapViewerPanel.DrawPencil)),
    addDrawing: (drawing: IMapDrawing) => dispatch(projectAddDrawing(drawing)),
    removeDrawing: (drawing: IMapDrawing) => dispatch(projectRemoveDrawing(drawing)),
    editDrawing: (drawing: IMapDrawing) => dispatch(projectAddPointToPolyline(drawing)),
    enableSelectionTool: () => dispatch(mapViewerSwitchEditPanel(MapViewerPanel.Selection))
  }
}

export const DrawPencilControl = connect(mapStateToProps, mapDispatchToProps)(DrawPencilControlView)
